import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';

// ==================== MUI Material & External Libraries ====================
import {
    alpha,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';

// ==================== Icons ====================
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import { CommonFilemanagerFileIcon } from 'components/CommonFileManager/CommonFilemanagerIcon';
import { ModernIconExpandDown, ModernIconExpandUp } from 'utils/icons';
import {
    CompleteChipIcon,
    DueCalanderIcon,
    InprogressChipIcon,
    ModernIconMore,
    OverDueCalanderIcon,
    PendingChipIcon,
    UserviewIcon,
    UrgentFlagIcon,
    HighFlagIcon,
    PriorityFlagIcon,
    ModerIconContractorView
} from 'utils/TaskManagementIcon/taskmanagemeneticon';

// ==================== API Imports ====================
import { useGetTaskModal, useGetTaskFiles, useGetFileUrl } from 'api/TaskManagement/taskManagement';
import { useGetTaskNoteList, useDeleteTaskNote, useGetTaskNoteFiles, useGetNoteFileUrl } from 'api/TaskManagement/TaskNote/taskNote';
import { TaskCardNoteAttachmentChips } from './TaskCard';

// ==================== Types, Utils & Components ====================
import { getAvatarThemeColor } from './TaskCard';
import { TaskItem, TaskNote, ViewTaskFormProps, getProjectFlagsForStage, getStatusName, getStageNameFromId, getPriorityName, TaskStatusEnum, getStatusEnumVal } from 'types/taskManagement/TaskManagement';
import { GetFormattedDateOnly, GetFormattedDateTime, GetFormattedTimeOnly, GetFriendlyTaskDueDate, stripAndDecodeHtml, cleanNoteHtml } from 'utils/commonFunctions';
import { showError } from 'utils/errorHandler';
import NoteForm from './NoteForm';
import TatChip from 'components/TaskManagement/TatChip';
import DeleteNoteModal from './DeleteNoteModal';
import dayjs from 'dayjs';
import { useGetPermissions } from 'api/authUser';
import { hasAddPermission, hasDeletePermission, hasEditPermission } from 'utils/permissions';

export default function ViewTaskForm({ open, closeModal, task: initialTask, projectName, onNoteChange }: ViewTaskFormProps) {
    const featureID = 47;
    const { permissions } = useGetPermissions();
    const allowAdd = hasAddPermission(featureID, permissions);
    const allowEdit = hasEditPermission(featureID, permissions);
    const allowDelete = hasDeletePermission(featureID, permissions);
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark'


    const { projectID } = useParams();
    const targetTaskId = initialTask?.taskID || null;
    const { data: taskDetail } = useGetTaskModal(open && targetTaskId ? targetTaskId : null);
    const { data: fetchedTaskFiles, isLoading: isTaskFilesLoading, isValidating: isTaskFilesValidating } = useGetTaskFiles(open && targetTaskId ? targetTaskId : null);
    const source = taskDetail || initialTask;

    const task = useMemo(() => {
        if (!source) return null;

        const title = source.taskTitle || source.title || '';
        const stage = source.stageName || getStageNameFromId(source.stageID) || source.stage || '';
        const projectFlags = {
            noc: source.projectFlags?.noc ?? source.noc ?? false,
            ntbo: source.projectFlags?.ntbo ?? source.ntbo ?? false,
            pp: source.projectFlags?.pp ?? source.pp ?? false,
            coc: source.projectFlags?.coc ?? source.coc ?? source.coC ?? false
        };

        const dueDate = source.dueOn || source.dueDate || '';
        const isOverdue = dueDate ? dayjs(dueDate).isBefore(dayjs(), 'day') : false;

        const assignedUser = source.assignedUsers && source.assignedUsers.length > 0 ? {
            name: source.assignedUsers[0].fullName || source.assignedUsers[0].userFullName || '',
            extraCount: source.assignedUsers.length - 1,
            extraUsers: source.assignedUsers.slice(1).map((u: any) => u.fullName || u.userFullName || '')
        } : (source.assignedUser || undefined);

        const status = getStatusName(source.status !== undefined ? source.status : source.taskStatus) || source.status;
        const priority = getPriorityName(source.priority);
        let description = source.descrFormatted || source.description || source.descrPlain || '';
        // Replace non-breaking spaces with standard spaces to allow word-wrapping
        description = description.replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ');

        const createdBy = source.createdByUserName ? {
            authorName: source.createdByUserName,
            createdDate: source.createdOn || ''
        } : (source.createdBy || undefined);

        const updatedBy = source.updatedByUserName ? {
            authorName: source.updatedByUserName,
            updatedDate: source.updatedOn || ''
        } : (source.updatedBy || undefined);

        const completedByUserName = source.completedByUserName || null;
        const completedOn = source.completedOn || null;
        const startedOn = source.startedOn || null;
        const elapsedMinutes = source.elapsedMinutes ?? (initialTask as any)?.elapsedMinutes ?? null;

        const notes = source.notes || [];

        const attachments = Array.isArray(fetchedTaskFiles)
            ? fetchedTaskFiles.map((f: any) => ({ id: String(f.fileID || f.id), name: f.fileName || f.name, type: 'doc' }))
            : (source.attachments || []);

        return {
            taskID: String(source.taskID || source.primaryKeyID || ''),
            title,
            stage,
            projectFlags,
            dueDate,
            isOverdue,
            assignedUser,
            status,
            priority,
            description,
            attachments,
            notes,
            createdBy,
            updatedBy,
            completedByUserName,
            completedOn,
            startedOn,
            elapsedMinutes,
            canEdit: source.canEdit ?? false,
            canDelete: source.canDelete ?? false,
            visibleToContractor: source.visibleToContractor ?? false
        } as TaskItem;
    }, [source, fetchedTaskFiles, initialTask]);

    const storedProjectName = sessionStorage.getItem('activeProjectName');
    const rawProjectID = projectID || (source as any)?.projectID || (source as any)?.projectId;
    const hasProject = Boolean(rawProjectID && Number(rawProjectID) > 0);
    const activeProjectName = hasProject ? (storedProjectName || (source as any)?.projectName || projectName || '') : '';
    const hasStage = Boolean(task?.stage && task.stage.trim() !== '');

    const [notesExpanded, setNotesExpanded] = useState<boolean>(true);
    const [openAddNote, setOpenAddNote] = useState<boolean>(false);
    const [selectedNoteForEdit, setSelectedNoteForEdit] = useState<TaskNote | null>(null);
    const [openDeleteNote, setOpenDeleteNote] = useState<boolean>(false);
    const [deletingNote, setDeletingNote] = useState<TaskNote | null>(null);

    // Hook to fetch main TASK Notes
    const { data: fetchedNotes, rawNotesData, mutate: mutateNotes } = useGetTaskNoteList(
        open && targetTaskId ? targetTaskId : '',
        null,
        Boolean(open && targetTaskId && (notesExpanded || openAddNote || openDeleteNote))
    );

    // =========================================================
    // [TASK FILES] Hook/Memo to prepare & process files for the main TASK
    // =========================================================
    const attachments = useMemo<{ id: string; name: string; size: number }[]>(() => {
        const rawFiles = fetchedTaskFiles && Array.isArray(fetchedTaskFiles) && fetchedTaskFiles.length > 0
            ? fetchedTaskFiles
            : (initialTask?.attachments || initialTask?.files || (source as any)?.attachments || (source as any)?.files || []);

        return (Array.isArray(rawFiles) ? rawFiles : [])
            .map((f: any) => ({
                id: String(f.id || f.fileID || f.fileId || f.primaryKeyID || ''),
                name: String(f.name || f.fileName || f.filename || 'File'),
                size: Number(f.size || f.fileSize || 0)
            }))
            .filter((f) => Boolean(f.id) && f.id !== 'undefined' && Boolean(f.name));
    }, [fetchedTaskFiles, initialTask, source]);

    const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);

    // =========================================================
    // [TASK FILES] Function to download a main TASK file
    // =========================================================
    const handleDownloadFile = async (fileID: string, fileName?: string) => {
        const targetTaskId = Number(initialTask?.taskID || source?.taskID || (source as any)?.primaryKeyID || task?.taskID);
        if (!targetTaskId) return;
        try {
            setDownloadingFileId(String(fileID));
            const res = await useGetFileUrl({ taskID: targetTaskId, fileID });
            const fileUrl = typeof res === 'string'
                ? res
                : (res?.fileUrl || res?.url || res?.data?.fileUrl || res?.data?.url || (typeof res?.data === 'string' ? res.data : null));
            if (fileUrl) {
                try {
                    const response = await fetch(fileUrl);
                    if (!response.ok) throw new Error('Download failed');
                    const blob = await response.blob();
                    const blobUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    if (fileName) link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(blobUrl);
                } catch {
                    const link = document.createElement('a');
                    link.href = fileUrl;
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    if (fileName) link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                }
            } else {
                showError('Download URL not available');
            }
        } catch (err) {
            console.error('File download error:', err);
        } finally {
            setDownloadingFileId(null);
        }
    };

    const notes = useMemo(() => {
        const rawList = rawNotesData !== undefined && Array.isArray(rawNotesData) ? rawNotesData : (task?.notes || []);
        return rawList.map((item: any) => {
            const name = item.createdByUserName || item.authorName || 'User';
            const initials = item.authorInitials || (name ? name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2) : 'U');
            const dateFormatted = item.createdOn
                ? `${GetFormattedDateOnly(item.createdOn)} on ${GetFormattedTimeOnly(item.createdOn)}`
                : item.createdDate || '';

            const rawNoteFiles = item.attachments || item.files || item.taskNoteFiles || item.taskFiles || item.getFiles || [];
            const noteAttachments = Array.isArray(rawNoteFiles)
                ? rawNoteFiles.map((f: any) => ({
                    id: String(f.id || f.fileID || f.fileId || f.primaryKeyID || ''),
                    name: f.name || f.fileName || f.filename || 'File',
                    size: f.size || f.fileSize || 0
                }))
                : [];

            return {
                id: String(item.taskNoteID || item.primaryKeyID || item.id),
                taskNoteID: item.taskNoteID || item.primaryKeyID || item.id,
                authorName: name,
                authorInitials: initials,
                createdDate: dateFormatted,
                rawCreatedOn: item.createdOn || item.createdDate,
                updatedOn: item.updatedOn || null,
                updatedByUserName: item.updatedByUserName || null,
                noteText: item.noteFormatted || item.notePlain || item.noteText || '',
                notePlain: item.notePlain || '',
                noteFormatted: item.noteFormatted || '',
                attachments: noteAttachments,
                canEdit: item.canEdit ?? true,
                canDelete: item.canDelete ?? true
            };
        });
    }, [fetchedNotes, task?.notes]);

    const latestNote = useMemo(() => {
        if (!notes || notes.length === 0) return null;
        if (notes.length === 1) return notes[0];
        return [...notes].sort((a: any, b: any) => {
            const timeA = a.rawCreatedOn ? new Date(a.rawCreatedOn).getTime() : 0;
            const timeB = b.rawCreatedOn ? new Date(b.rawCreatedOn).getTime() : 0;
            if (timeA !== timeB && !isNaN(timeA) && !isNaN(timeB)) {
                return timeB - timeA;
            }
            const idA = Number(a.taskNoteID || a.id || 0);
            const idB = Number(b.taskNoteID || b.id || 0);
            return idB - idA;
        })[0];
    }, [notes]);

    // Note More Menu State
    const [noteMenuAnchor, setNoteMenuAnchor] = useState<null | HTMLElement>(null);
    const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);

    const selectedNote = useMemo(() => {
        return notes.find((n: any) => String(n.id) === String(selectedNoteId));
    }, [notes, selectedNoteId]);

    const handleOpenNoteMenu = (event: React.MouseEvent<HTMLElement>, noteId: string) => {
        event.stopPropagation();
        setNoteMenuAnchor(event.currentTarget);
        setSelectedNoteId(noteId);
    };

    const handleCloseNoteMenu = () => {
        setNoteMenuAnchor(null);
        setSelectedNoteId(null);
    };

    const handleEditNoteClick = () => {
        const noteToEdit = notes.find((n: any) => String(n.id) === String(selectedNoteId));
        if (noteToEdit) {
            setSelectedNoteForEdit(noteToEdit as any);
            setOpenAddNote(true);
        }
        handleCloseNoteMenu();
    };

    const handleDeleteNoteClick = () => {
        const noteToDelete = notes.find((n: any) => String(n.id) === String(selectedNoteId));
        if (noteToDelete) {
            setDeletingNote(noteToDelete as any);
            setOpenDeleteNote(true);
        }
        handleCloseNoteMenu();
    };

    const handleOpenAddNoteModal = () => {
        setSelectedNoteForEdit(null);
        setOpenAddNote(true);
    };

    if (!initialTask || !task) return null;

    return (
        <>
            <Dialog
                open={open}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                        closeModal();
                    }
                }}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2 }
                }}
            >
                {/* Header */}
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" component="span">
                        <Typography component="span" sx={{ color: 'primary.main' }}>
                            View
                        </Typography>{' '}
                        Task
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={closeModal}
                        sx={{
                            color: 'primary.main',
                            '&:hover': { bgcolor: 'primary.lighter' }
                        }}
                    >
                        <CloseOutlined />
                    </IconButton>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ p: 3 }}>
                    <Stack spacing={2.5}>
                        {/* Section 1: Project Name & Stage Flags Row */}
                        {(hasProject || hasStage) && (
                            <>
                                <Grid container spacing={3} alignItems="center">
                                    {hasProject && (
                                        <Grid size={{ xs: 12, sm: hasStage ? 4 : 12 }}>
                                            <Stack spacing={0.5}>
                                                <Typography variant="h6" color="text.secondary" fontSize={13}>
                                                    Project Name
                                                </Typography>
                                                <Typography variant="subtitle1" fontWeight={500} fontSize={15} color="text.primary">
                                                    {activeProjectName}
                                                </Typography>
                                            </Stack>
                                        </Grid>
                                    )}

                                    {hasProject && hasStage && (
                                        <Box
                                            sx={{
                                                width: '2px',
                                                height: 60,
                                                bgcolor: 'grey.300',
                                                borderRadius: '1px',
                                                alignSelf: 'center',
                                                opacity: 0.5
                                            }}
                                        />
                                    )}

                                    {hasStage && (
                                        <Grid size={{ xs: 12, sm: hasProject ? 7 : 12 }}>
                                            <Stack spacing={0.5} sx={{ marginLeft: hasProject ? -0.9 : 0 }}>
                                                <Typography variant="h6" color="text.secondary" fontSize={13} fontWeight={400}>
                                                    Stage
                                                </Typography>
                                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={1}>
                                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                                        <Typography variant="subtitle1" fontWeight={400}>
                                                            {task.stage}
                                                        </Typography>
                                                    </Stack>

                                                    {/* Project Flags Chips */}
                                                    {getProjectFlagsForStage(task?.stage).map((flag) => {
                                                        const flagValues = task?.projectFlags || { noc: true, ntbo: true, pp: false, coc: true };
                                                        const isChecked = Boolean((flagValues as any)[flag.key]);
                                                        return (
                                                            <Chip
                                                                key={flag.key}
                                                                icon={
                                                                    isChecked ? (
                                                                        <CheckOutlined style={{ color: '#1677FF', fontSize: '13px', fontWeight: 'bold' }} />
                                                                    ) : (
                                                                        <CloseOutlined style={{ color: '#8C8C8C', fontSize: '11px' }} />
                                                                    )
                                                                }
                                                                label={flag.label}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{
                                                                    height: 28,
                                                                    bgcolor: isDark ? '#151515' : '#ffffff',
                                                                    borderColor: 'grey.300',
                                                                    borderRadius: '4px',
                                                                    color: 'text.primary',
                                                                    fontWeight: 400,
                                                                    fontSize: '13px',
                                                                    px: 0.5,
                                                                    '& .MuiChip-icon': {
                                                                        ml: 0.75,
                                                                        mr: -0.25
                                                                    }
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </Stack>
                                            </Stack>
                                        </Grid>
                                    )}
                                </Grid>

                                <Divider />
                            </>
                        )}

                        {/* Task Details Content Block (Tight Spacing) */}
                        <Stack spacing={1.25}>
                            {/* Section 2: Task Title & Status Chip */}
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                                <Stack spacing={0.75} sx={{ flexGrow: 1 }}>
                                    <Typography
                                        title={task.title}
                                        variant="h6"
                                        sx={{
                                            fontWeight: 400,
                                            fontSize: '17px',
                                            color: 'primary.main',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: '500px'
                                        }}
                                    >
                                        {task.title}
                                    </Typography>

                                    {/* Sub-info line: Due date & Assigned users */}
                                    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5}>
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            {task.isOverdue && getStatusEnumVal(task.status) !== TaskStatusEnum.Completed ? <OverDueCalanderIcon /> : <DueCalanderIcon />}
                                            <Typography
                                                variant="caption"
                                                fontSize="14px"
                                                sx={{
                                                    color: task.isOverdue ? 'error.main' : 'text.secondary',
                                                    fontWeight: task.isOverdue ? 500 : 400
                                                }}
                                            >
                                                {getStatusEnumVal(task.status) !== TaskStatusEnum.Completed ? task.isOverdue ? 'Overdue : ' : 'Due : ' : null}
                                                <Box component="span" sx={{ color: 'text.primary', fontSize: '14px', fontWeight: 400 }}>
                                                    {GetFriendlyTaskDueDate(task.dueDate)}
                                                </Box>
                                            </Typography>
                                        </Stack>

                                        {task.priority && (
                                            <>
                                                <Box sx={{ width: '2px', height: 18, bgcolor: 'grey.300', borderRadius: '1px' }} />
                                                {task.priority === 'Urgent' && (
                                                    <Chip
                                                        icon={<HighFlagIcon />}
                                                        label="Urgent"
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            borderColor: 'divider',
                                                            height: 24,
                                                            fontSize: '13px',
                                                            fontWeight: 500,
                                                            borderRadius: 1,
                                                            '& .MuiChip-icon': { ml: 0.5 },
                                                            p: '10px 6px',
                                                        }}
                                                    />
                                                )}
                                                {task.priority === 'High' && (
                                                    <Chip
                                                        icon={<UrgentFlagIcon />}
                                                        label="High"
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            borderColor: 'divider',
                                                            height: 24,
                                                            fontSize: '13px',
                                                            fontWeight: 500,
                                                            borderRadius: 1,
                                                            '& .MuiChip-icon': { ml: 0.5 },
                                                            p: '10px 6px',
                                                        }}
                                                    />
                                                )}
                                                {task.priority === 'Low' && (
                                                    <Chip
                                                        icon={<PriorityFlagIcon color="#8C8C8C" />}
                                                        label="Low"
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            borderColor: 'divider',
                                                            height: 24,
                                                            fontSize: '13px',
                                                            fontWeight: 500,
                                                            borderRadius: 1,
                                                            '& .MuiChip-icon': { ml: 0.5 },
                                                            p: '10px 6px',
                                                        }}
                                                    />
                                                )}
                                                {task.priority === 'Normal' && (
                                                    <Chip
                                                        icon={<PriorityFlagIcon color="#1677FF" />}
                                                        label="Normal"
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            borderColor: 'divider',
                                                            height: 24,
                                                            fontSize: '13px',
                                                            fontWeight: 500,
                                                            borderRadius: 1,
                                                            '& .MuiChip-icon': { ml: 0.5 },
                                                            p: '10px 6px',
                                                        }}
                                                    />
                                                )}
                                            </>
                                        )}

                                        {task.assignedUser && (
                                            <>
                                                <Box sx={{ width: '2px', height: 18, bgcolor: 'grey.300', borderRadius: '1px' }} />

                                                {/* Assigned User */}
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <UserviewIcon style={{ width: 18, height: 18 }} />
                                                    <Typography variant="caption" fontSize="14px" color="text.primary" fontWeight={400}>
                                                        {task.assignedUser?.name}
                                                    </Typography>
                                                    {task.assignedUser?.extraCount ? (
                                                        <Tooltip
                                                            title={
                                                                <Stack spacing={0.5} sx={{ p: 0.5 }}>
                                                                    {(task.assignedUser.extraUsers || []).map((item, index) => (
                                                                        <Typography
                                                                            key={index}
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontSize: '15px',
                                                                                fontWeight: 400,
                                                                                color: 'text.primary',
                                                                                lineHeight: 1.4,
                                                                                whiteSpace: 'nowrap'
                                                                            }}
                                                                        >
                                                                            {item}
                                                                        </Typography>
                                                                    ))}
                                                                </Stack>
                                                            }
                                                            placement="bottom"
                                                            arrow
                                                            enterTouchDelay={0}
                                                            componentsProps={{
                                                                tooltip: {
                                                                    sx: {
                                                                        bgcolor: 'background.paper',
                                                                        color: 'text.primary',
                                                                        boxShadow: theme.customShadows?.z1 || '0px 6px 20px rgba(0, 0, 0, 0.1)',
                                                                        borderRadius: '8px',
                                                                        border: '1px solid',
                                                                        borderColor: 'divider',
                                                                        p: 1,
                                                                        minWidth: 120
                                                                    }
                                                                },
                                                                arrow: {
                                                                    sx: {
                                                                        color: 'background.paper',
                                                                        '&::before': {
                                                                            border: '1px solid',
                                                                            borderColor: 'divider',
                                                                            backgroundColor: 'background.paper'
                                                                        }
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            <Chip
                                                                label={`+${task.assignedUser.extraCount} more`}
                                                                size="small"
                                                                sx={{
                                                                    height: 22,
                                                                    fontSize: '11px',
                                                                    bgcolor: 'primary.lighter',
                                                                    color: 'primary.main',
                                                                    fontWeight: 500,
                                                                    cursor: 'pointer'
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    ) : null}
                                                </Stack>
                                            </>
                                        )}
                                        {task.visibleToContractor && (
                                            <>
                                                <Box
                                                    sx={{
                                                        width: '2px',
                                                        height: 20,
                                                        bgcolor: 'grey.300',
                                                        borderRadius: '1px',
                                                        alignSelf: 'center'
                                                    }}
                                                />

                                                <Stack title="Contractor can view this task" direction="row" spacing={0.75} alignItems="center">
                                                    <ModerIconContractorView />
                                                </Stack>
                                            </>
                                        )}
                                    </Stack>
                                </Stack>

                                {/* Right Side: TAT + Status Chip */}
                                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0, mt: 0.5 }}>
                                    <TatChip
                                        variant="text"
                                        startedOn={task.startedOn}
                                        completedOn={task.completedOn}
                                        elapsedMinutes={task.elapsedMinutes}
                                        showIcon={false}
                                    />

                                    {/* Status Chip */}
                                    {task.status === 'In-Progress' && (
                                        <Chip
                                            icon={<InprogressChipIcon style={{ width: 18, height: 18 }} />}
                                            label="In Progress"
                                            sx={{
                                                bgcolor: isDark ? 'rgba(22, 119, 255, 0.15)' : 'primary.lighter',
                                                color: 'primary.main',
                                                border: '1px solid',
                                                borderColor: isDark ? 'transparent' : 'primary.lighter',
                                                borderRadius: '4px',
                                                fontWeight: 400,
                                                height: 32,
                                                px: 1,
                                                fontSize: '14px',
                                                '& .MuiChip-icon': { ml: 0.5, mr: -0.5 },
                                                '& .MuiChip-label': { pl: 0.75, pr: 0.5 }
                                            }}
                                        />
                                    )}
                                    {task.status === 'Pending' && (
                                        <Chip
                                            icon={<PendingChipIcon style={{ width: 18, height: 18 }} />}
                                            label="Pending"
                                            sx={{
                                                bgcolor: isDark ? 'rgba(254, 119, 17, 0.15)' : 'warning.lighter',
                                                color: 'warning.main',
                                                border: '1px solid',
                                                borderColor: isDark ? 'transparent' : 'warning.lighter',
                                                borderRadius: '4px',
                                                fontWeight: 400,
                                                height: 32,
                                                px: 1,
                                                fontSize: '14px',
                                                '& .MuiChip-icon': { ml: 0.5, mr: -0.5 },
                                                '& .MuiChip-label': { pl: 0.75, pr: 0.5 }
                                            }}
                                        />
                                    )}
                                    {task.status === 'Completed' && (
                                        <Chip
                                            icon={<CompleteChipIcon style={{ width: 18, height: 18 }} />}
                                            label="Completed"
                                            sx={{
                                                bgcolor: isDark ? 'rgba(56, 163, 79, 0.15)' : 'success.lighter',
                                                color: 'success.main',
                                                border: '1px solid',
                                                borderColor: isDark ? 'transparent' : 'success.lighter',
                                                borderRadius: '4px',
                                                fontWeight: 400,
                                                height: 32,
                                                px: 1,
                                                fontSize: '14px',
                                                '& .MuiChip-icon': { ml: 0.5, mr: -0.5 },
                                                '& .MuiChip-label': { pl: 0.75, pr: 0.5 }
                                            }}
                                        />
                                    )}
                                    {task.status === 'Cancelled' && (
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.6,
                                                height: isDark ? 31 : 36,
                                                px: 1,
                                                bgcolor: isDark ? 'rgba(56, 163, 79, 0.15)' : 'error.lighter',
                                                color: 'error.main',
                                                borderRadius: '4px',
                                                whiteSpace: 'nowrap',
                                                flexShrink: 0
                                            }}
                                        >
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                <CloseOutlined />
                                            </Box>
                                            <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 500, fontSize: '13px', lineHeight: 1 }}>
                                                Cancelled
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </Stack>

                            {/* Section 3: Task Description */}
                            {task.description && (
                                <Box
                                    sx={{
                                        fontSize: '13.5px',
                                        lineHeight: 1.6,
                                        color: 'text.primary',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        pr: 0.5,
                                        cursor: 'pointer',
                                        '& table': {
                                            borderCollapse: 'collapse',
                                            width: '100%',
                                            my: 1,
                                            '& td, & th': {
                                                border: `1px solid ${theme.palette.divider}`,
                                                p: 1,
                                                fontSize: '13px'
                                            },
                                            '& img': {
                                                maxHeight: '40px',
                                                maxWidth: '60px',
                                                width: 'auto',
                                                height: 'auto',
                                                objectFit: 'contain',
                                                verticalAlign: 'middle'
                                            }
                                        },
                                        '& img': {
                                            maxWidth: '100%',
                                            maxHeight: '200px',
                                            height: 'auto',
                                            objectFit: 'contain',
                                            borderRadius: 1
                                        },
                                        '& p': {
                                            m: 0,
                                            mb: 0.5
                                        }
                                    }}
                                    title={stripAndDecodeHtml(task.description)}
                                    dangerouslySetInnerHTML={{ __html: task.description }}
                                />
                            )}

                            {/* Section 4: Main Task Attachments Chips (+More) */}
                            {(isTaskFilesLoading && (!fetchedTaskFiles || (Array.isArray(fetchedTaskFiles) && fetchedTaskFiles.length === 0))) ? (
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.5, minHeight: 36 }}>
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            px: 1.25,
                                            py: 0.4,
                                            borderRadius: '50px',
                                            bgcolor: isDark ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.primary.main, 0.06),
                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                                            boxShadow: `0 2px 6px ${alpha(theme.palette.primary.main, 0.08)}`,
                                            transition: 'all 0.3s ease-in-out'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                border: `2px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                                                borderTopColor: theme.palette.primary.main,
                                                animation: 'spinCircle 0.8s linear infinite',
                                                '@keyframes spinCircle': {
                                                    '0%': { transform: 'rotate(0deg)' },
                                                    '100%': { transform: 'rotate(360deg)' }
                                                }
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: '11.5px',
                                                fontWeight: 600,
                                                color: 'primary.main',
                                                letterSpacing: '0.2px'
                                            }}
                                        >
                                            Loading files...
                                        </Typography>
                                    </Box>
                                </Stack>
                            ) : (
                                <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" sx={{ minHeight: attachments && attachments.length > 0 ? 36 : 'auto' }}>
                                    {attachments && attachments.length > 0 && attachments.slice(0, 3).map((file: { id: string; name: string; size?: number }) => {
                                        const isDownloading = downloadingFileId === String(file.id);
                                        return (
                                            <Chip
                                                key={file.id}
                                                title={file.name}
                                                icon={
                                                    isDownloading ? (
                                                        <CircularProgress size={14} thickness={5} color="primary" sx={{ ml: 0.75, mr: -0.5 }} />
                                                    ) : (
                                                        <CommonFilemanagerFileIcon name={file.name} type="file" />
                                                    )
                                                }
                                                label={isDownloading ? 'Downloading...' : file.name}
                                                disabled={isDownloading}
                                                variant="outlined"
                                                onClick={() => {
                                                    if (!isDownloading) {
                                                        handleDownloadFile(String(file.id), file.name);
                                                    }
                                                }}
                                                sx={{
                                                    height: 32,
                                                    bgcolor: 'grey.50',
                                                    borderColor: 'divider',
                                                    borderRadius: '4px',
                                                    fontSize: '13px',
                                                    color: 'text.primary',
                                                    fontWeight: 400,
                                                    cursor: isDownloading ? 'default' : 'pointer',
                                                    '& .MuiChip-icon': { ml: 0.75, mr: -0.5 },
                                                    '& .MuiChip-label': { pl: 0.75, pr: 0.75 },
                                                    '&:hover': { bgcolor: 'grey.100' },
                                                    maxWidth: '250px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            />
                                        );
                                    })}

                                    {attachments.length > 3 && (
                                        <Tooltip
                                            title={
                                                <Stack spacing={0.75} sx={{ p: 0.5 }}>
                                                    {attachments.slice(3).map((file: { id: string; name: string; size?: number }) => {
                                                        const isDownloading = downloadingFileId === String(file.id);
                                                        return (
                                                            <Chip
                                                                key={file.id}
                                                                icon={
                                                                    isDownloading ? (
                                                                        <CircularProgress size={12} thickness={5} color="primary" sx={{ ml: 0.5, mr: -0.5 }} />
                                                                    ) : (
                                                                        <CommonFilemanagerFileIcon name={file.name} type="file" />
                                                                    )
                                                                }
                                                                label={isDownloading ? 'Downloading...' : file.name}
                                                                title={file.name}
                                                                disabled={isDownloading}
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => {
                                                                    if (!isDownloading) {
                                                                        handleDownloadFile(String(file.id), file.name);
                                                                    }
                                                                }}
                                                                sx={{
                                                                    height: 26,
                                                                    bgcolor: 'grey.50',
                                                                    borderColor: 'divider',
                                                                    borderRadius: 1.5,
                                                                    fontSize: '11.5px',
                                                                    color: 'text.primary',
                                                                    fontWeight: 500,
                                                                    cursor: isDownloading ? 'default' : 'pointer',
                                                                    '& .MuiChip-icon': { ml: 0.5, mr: -0.5 },
                                                                    '& .MuiChip-label': { pl: 0.5, pr: 0.5 },
                                                                    '&:hover': { bgcolor: 'grey.100' },
                                                                    maxWidth: '250px',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </Stack>
                                            }
                                            placement="bottom"
                                            arrow
                                            enterTouchDelay={0}
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        bgcolor: 'background.paper',
                                                        color: 'text.primary',
                                                        boxShadow: theme.customShadows?.z1 || '0px 6px 20px rgba(0, 0, 0, 0.1)',
                                                        borderRadius: '8px',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        p: 1.25,
                                                        minWidth: 150
                                                    }
                                                },
                                                arrow: {
                                                    sx: {
                                                        color: 'background.paper',
                                                        '&::before': {
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            backgroundColor: 'background.paper'
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            <Chip
                                                label={`+${attachments.length - 3} more`}
                                                size="small"
                                                sx={{
                                                    height: 22,
                                                    fontSize: '11px',
                                                    bgcolor: 'primary.lighter',
                                                    color: 'primary.main',
                                                    fontWeight: 500,
                                                    cursor: 'pointer'
                                                }}
                                            />
                                        </Tooltip>
                                    )}

                                    {(isTaskFilesLoading || isTaskFilesValidating) && (
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                p: 0.5
                                            }}
                                        >
                                            <CircularProgress size={14} thickness={4} color="primary" />
                                        </Box>
                                    )}
                                </Stack>
                            )}
                        </Stack>

                        {/* Section 5: Notes Container */}
                        {notes && notes.length > 0 && (
                            <Box
                                sx={{
                                    bgcolor: 'grey.50',
                                    p: 0.5,
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}
                            >
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    onClick={() => setNotesExpanded((prev) => !prev)}
                                    sx={{ cursor: 'pointer', mb: notesExpanded ? 1.5 : 0 }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            color: notesExpanded ? 'text.primary' : 'text.secondary',
                                            fontSize: notesExpanded ? '14px' : '13px',
                                            fontWeight: notesExpanded ? 600 : 400,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            pr: 1
                                        }}
                                    >
                                        {notesExpanded ? 'Notes' : stripAndDecodeHtml(latestNote?.notePlain || latestNote?.noteText)}
                                    </Typography>
                                    <IconButton size="small" sx={{ color: 'text.secondary' }}>
                                        {notesExpanded ? <ModernIconExpandUp /> : <ModernIconExpandDown />}
                                    </IconButton>
                                </Stack>

                                <Collapse in={notesExpanded}>
                                    <Stack spacing={0}>
                                        {notes.map((note, idx) => (
                                            <Box key={note.id || idx}>
                                                {idx > 0 && <Divider sx={{ my: 0.75 }} />}
                                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                    <Stack direction="row" spacing={1.25} alignItems="center">
                                                        <Avatar
                                                            sx={{
                                                                width: 28,
                                                                height: 28,
                                                                fontSize: '11px',
                                                                bgcolor: getAvatarThemeColor(note.authorName),
                                                                color: '#fff',
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            {note.authorInitials}
                                                        </Avatar>
                                                        <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                                                            {note.authorName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" fontSize={12}>
                                                            {note.createdDate}
                                                        </Typography>
                                                    </Stack>

                                                    {(allowEdit && note.canEdit !== false) || (allowDelete && note.canDelete !== false) ? (
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => handleOpenNoteMenu(e, note.id)}
                                                            sx={{ color: 'text.secondary' }}
                                                        >
                                                            <ModernIconMore />
                                                        </IconButton>
                                                    ) : null}
                                                </Stack>
                                                {/* Note content */}
                                                <Box sx={{ pl: 4, mt: 0, pr: 1, maxWidth: '100%', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        fontSize={13}
                                                        fontWeight={400}
                                                        component="div"
                                                        dangerouslySetInnerHTML={{ __html: cleanNoteHtml(note.noteText) }}
                                                        sx={{
                                                            wordBreak: 'break-word',
                                                            overflowWrap: 'anywhere',
                                                            '& p': { m: 0, p: 0, wordBreak: 'break-word', overflowWrap: 'anywhere' },
                                                            '& table': {
                                                                borderCollapse: 'collapse',
                                                                width: '100%',
                                                                my: 1,
                                                                border: '1px solid',
                                                                borderColor: 'divider'
                                                            },
                                                            '& th, & td': {
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                padding: '6px 10px',
                                                                textAlign: 'left',
                                                                fontSize: '12.5px'
                                                            },
                                                            '& th': {
                                                                bgcolor: 'grey.100',
                                                                fontWeight: 600
                                                            },
                                                            '& img': {
                                                                maxWidth: '100%',
                                                                height: 'auto',
                                                                borderRadius: 1,
                                                                my: 0.5
                                                            }
                                                        }}
                                                    />

                                                    {/* Note attachment chips fetched dynamically via TaskNote API */}
                                                    <TaskCardNoteAttachmentChips taskNoteID={note.taskNoteID} initialFiles={note.attachments} />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Collapse>
                            </Box>
                        )}

                        {/* + Add Note Link */}
                        {allowAdd && (
                            <Box>
                                <Button
                                    onClick={handleOpenAddNoteModal}
                                    startIcon={<PlusOutlined style={{ strokeWidth: 2, fontSize: '13px' }} />}
                                    size="small"
                                    sx={{
                                        color: 'primary.main',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        p: 0,
                                        minWidth: 0,
                                        '&:hover': { bgcolor: 'transparent' }
                                    }}
                                >
                                    Add Note
                                </Button>
                            </Box>
                        )}

                    </Stack>
                </DialogContent>

                <Divider />

                {/* Footer Actions */}
                <DialogActions sx={{ p: 2.5, px: 3, justifyContent: 'space-between' }}>
                    <Box>
                        {Boolean(
                            task.createdBy ||
                            task.updatedBy ||
                            task.completedByUserName ||
                            (source as any)?.completedByUserName ||
                            (task as any)?.createdByUserName ||
                            (task as any)?.updatedByUserName
                        ) && (
                                <Typography variant="caption" color="text.secondary" fontSize={13}>
                                    {task.status === 'Completed'
                                        ? 'Completed by '
                                        : ((task.updatedBy?.authorName || (task as any)?.updatedByUserName) ? 'Updated by ' : 'Created by ')}
                                    <Typography component="span" sx={{ color: 'primary.main', fontStyle: 'italic', fontSize: '12px' }}>
                                        {task.status === 'Completed'
                                            ? (task.completedByUserName || (source as any)?.completedByUserName || task.updatedBy?.authorName || (task as any)?.updatedByUserName || task.createdBy?.authorName || (task as any)?.createdByUserName)
                                            : (task.updatedBy?.authorName || (task as any)?.updatedByUserName || task.createdBy?.authorName || (task as any)?.createdByUserName)}
                                    </Typography>{' '}
                                    on{' '}
                                    <Typography component="span" sx={{ color: 'primary.main', fontStyle: 'italic', fontSize: '12px' }}>
                                        {GetFormattedDateTime(
                                            task.status === 'Completed'
                                                ? (task.completedOn || (source as any)?.completedOn || task.updatedBy?.updatedDate || (task as any)?.updatedOn || task.createdBy?.createdDate || (task as any)?.createdOn)
                                                : (task.updatedBy?.updatedDate || (task as any)?.updatedOn || task.createdBy?.createdDate || (task as any)?.createdOn)
                                        )}
                                    </Typography>
                                </Typography>
                            )}
                    </Box>

                    <Button
                        variant="outlined"
                        onClick={closeModal}
                        sx={{
                            borderRadius: 1.5,
                            px: 3,
                            py: 1,
                            textTransform: 'none',
                            color: theme.palette.grey[500],
                            borderColor: 'grey.300',
                            fontWeight: 500,
                            '&:hover': { bgcolor: 'grey.100', borderColor: 'grey.400', color: theme.palette.grey[600] }
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Note Options Context Menu */}
            {(allowEdit && selectedNote?.canEdit !== false) || (allowDelete && selectedNote?.canDelete !== false) ? (
                <Menu
                    anchorEl={noteMenuAnchor}
                    open={Boolean(noteMenuAnchor)}
                    onClose={handleCloseNoteMenu}
                    PaperProps={{
                        elevation: 0,
                        sx: {
                            borderRadius: '8px',
                            minWidth: 130,
                            p: 0.75,
                            border: '1px solid',
                            borderColor: theme.palette.grey[300],
                            boxShadow: theme.customShadows?.z1 || '0px 8px 24px rgba(0, 0, 0, 0.12)',
                            '& .MuiList-root': {
                                p: 0
                            }
                        }
                    }}
                >
                    {allowEdit && selectedNote?.canEdit !== false && (
                        <MenuItem onClick={handleEditNoteClick} sx={{ borderRadius: '4px', fontSize: '13px', py: 0.75, px: 1.5 }}>
                            <EditOutlined style={{ marginRight: 8, fontSize: 16, color: theme.palette.primary.main }} /> Edit
                        </MenuItem>
                    )}

                    {allowDelete && selectedNote?.canDelete !== false && (
                        <MenuItem onClick={handleDeleteNoteClick} sx={{ borderRadius: '4px', fontSize: '13px', py: 0.75, px: 1.5 }}>
                            <DeleteOutlined style={{ marginRight: 8, fontSize: 16, color: theme.palette.error.main }} /> Delete
                        </MenuItem>
                    )}
                </Menu>
            ) : null}
            {/* Add / Edit Note Modal Form (ViewTaskForm remains open underneath) */}
            {openAddNote && (
                <NoteForm
                    open={openAddNote}
                    closeModal={() => {
                        setOpenAddNote(false);
                        setSelectedNoteForEdit(null);
                        mutateNotes();
                        if (onNoteChange) onNoteChange();
                    }}
                    taskId={task?.taskID || initialTask?.taskID}
                    selectedNote={selectedNoteForEdit}
                />
            )}

            {/* Delete Note Modal */}
            {openDeleteNote && (
                <DeleteNoteModal
                    open={openDeleteNote}
                    onClose={() => {
                        setOpenDeleteNote(false);
                        setDeletingNote(null);
                    }}
                    onConfirm={async () => {
                        if (deletingNote) {
                            const noteId = (deletingNote as any).taskNoteID || deletingNote.id;
                            await useDeleteTaskNote(noteId);
                            await mutateNotes();
                            if (onNoteChange) onNoteChange();
                        }
                        setOpenDeleteNote(false);
                        setDeletingNote(null);
                    }}
                    filesCount={deletingNote?.attachments?.length || 0}
                />
            )}
        </>
    );
}
